import React, { useState, useEffect, useMemo } from 'react';
import { getIncidents } from '../services/apiService';
import { Incident, IncidentStatus, User, Severity, EntityType } from '../types';
import { LoadingSpinner, AlertTriangleIcon } from './icons';
import IncidentDetailModal from './IncidentDetailModal';

const statusColors: Record<IncidentStatus, string> = {
  [IncidentStatus.OPEN]: 'bg-blue-100 text-blue-800',
  [IncidentStatus.UNDER_REVIEW]: 'bg-yellow-100 text-yellow-800',
  [IncidentStatus.DISPUTED]: 'bg-orange-100 text-orange-800',
  [IncidentStatus.RESOLVED]: 'bg-green-100 text-green-800',
};

const severityColors: Record<Severity, string> = {
    [Severity.MINOR]: 'bg-gray-200 text-gray-800',
    [Severity.MAJOR]: 'bg-yellow-400 text-yellow-900',
    [Severity.CRITICAL]: 'bg-red-500 text-white',
};

const ReportCard: React.FC<{incident: Incident, onClick: () => void, onViewProfile: (e: React.MouseEvent) => void}> = ({ incident, onClick, onViewProfile }) => (
    <div onClick={onClick} className="bg-white p-4 rounded-lg shadow border border-gray-200 cursor-pointer hover:shadow-md transition-shadow">
        <div className="flex justify-between items-start mb-2">
            <button onClick={onViewProfile} className="font-bold text-primary-dark hover:underline text-left">
                {incident.entityDetails.name}
            </button>
            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[incident.status]}`}>
                {incident.status}
            </span>
        </div>
        <div className="text-sm text-gray-600 mb-2">
            <p><span className="font-semibold">Type:</span> {incident.incidentType}</p>
            <p><span className="font-semibold">Date:</span> {new Date(incident.incidentDate).toLocaleDateString()}</p>
        </div>
         <div className="flex justify-end items-center text-xs text-gray-500">
            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${severityColors[incident.severity]}`}>
                {incident.severity}
            </span>
        </div>
    </div>
);


interface MyReportsPageProps {
    user: User;
    onViewProfile: (entityId: string, entityType: EntityType) => void;
}

const MyReportsPage: React.FC<MyReportsPageProps> = ({ user, onViewProfile }) => {
  const [allIncidents, setAllIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);

  useEffect(() => {
    const fetchIncidents = async () => {
      try {
        const data = await getIncidents();
        // Filter for user's organization right away
        const myReports = data.filter(inc => inc.reporterOrgId === user.organizationId);
        setAllIncidents(myReports);
      } catch (err) {
        setError('Failed to fetch your reports.');
      } finally {
        setLoading(false);
      }
    };
    fetchIncidents();
  }, [user.organizationId]);

  const filteredIncidents = useMemo(() => {
    if (!searchTerm) {
        return allIncidents;
    }
    const term = searchTerm.toLowerCase();
    return allIncidents.filter(incident =>
      incident.id.toLowerCase().includes(term) ||
      incident.entityDetails.name.toLowerCase().includes(term) ||
      incident.incidentType.toLowerCase().includes(term)
    );
  }, [allIncidents, searchTerm]);

  const handleIncidentUpdate = (updatedIncident: Incident) => {
    setAllIncidents(prev => prev.map(inc => inc.id === updatedIncident.id ? updatedIncident : inc));
    setSelectedIncident(updatedIncident);
  };
  
  const handleIncidentDelete = (incidentId: string) => {
    setAllIncidents(prev => prev.filter(inc => inc.id !== incidentId));
  };

  const handleCloseModal = () => {
      setSelectedIncident(null);
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">My Reported Incidents</h1>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search your reports by ID, Name, or Type..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full md:w-1/2 p-2 border border-gray-300 rounded-md focus:ring-primary-light focus:border-primary-light"
        />
      </div>

      {loading && <div className="flex justify-center items-center py-8"><LoadingSpinner className="w-8 h-8 text-primary" /></div>}
      {error && <div className="text-red-500 p-4 bg-red-100 rounded-md">{error}</div>}

      {!loading && !error && (
        <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Incident ID</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entity</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {filteredIncidents.map(incident => (
                        <tr key={incident.id} onClick={() => setSelectedIncident(incident)} className="hover:bg-gray-50 cursor-pointer">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-700">{incident.id}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <button
                                onClick={(e) => { e.stopPropagation(); onViewProfile(incident.entityId, incident.entityType); }}
                                className="text-sm font-medium text-primary-dark hover:underline text-left"
                            >
                                {incident.entityDetails.name}
                            </button>
                            <div className="text-sm text-gray-500">{incident.entityType}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{incident.incidentType}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${severityColors[incident.severity]}`}>
                            {incident.severity}
                            {incident.severity === Severity.CRITICAL && <AlertTriangleIcon className="w-4 h-4 ml-1 inline-block" />}
                            </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[incident.status]}`}>
                            {incident.status}
                            </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(incident.incidentDate).toLocaleDateString()}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
                {filteredIncidents.length === 0 && <p className="text-center text-gray-500 py-8">No reports found matching your search.</p>}
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
                {filteredIncidents.length > 0 ? filteredIncidents.map(incident => (
                    <ReportCard 
                        key={incident.id}
                        incident={incident}
                        onClick={() => setSelectedIncident(incident)}
                        onViewProfile={(e) => { e.stopPropagation(); onViewProfile(incident.entityId, incident.entityType); }}
                    />
                )) : (
                     <p className="text-center text-gray-500 py-8">No reports found matching your search.</p>
                )}
            </div>
        </>
      )}
      
      {selectedIncident && (
        <IncidentDetailModal 
          incident={selectedIncident} 
          user={user} 
          onClose={handleCloseModal}
          onUpdate={handleIncidentUpdate}
          onDelete={handleIncidentDelete}
        />
      )}
    </div>
  );
};

export default MyReportsPage;
